import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import type { OrganizationMemberRoleName } from "@multi-chat/shared";
import { buildReportMessagesExportPath } from "../../api/reports-api.js";
import { isApiError } from "../../api/client.js";
import { ErrorState } from "../../components/common/ErrorState/index.js";
import { outgoingReportEmptyKind } from "./report-empty-kind.js";
import { OUTGOING_REPORT_COLUMNS } from "./report-column-defs.js";
import { MessageDetailDrawer } from "./MessageDetailDrawer.js";
import { OutgoingReportSummary } from "./OutgoingReportSummary.js";
import { OutgoingReportTable } from "./OutgoingReportTable.js";
import { OutgoingReportToolbar } from "./OutgoingReportToolbar.js";
import { ReportColumnsPopover } from "./ReportColumnsPopover.js";
import { ReportExportMenu } from "./ReportExportMenu.js";
import { ReportPageChrome } from "./ReportPageChrome.js";
import { ReportPageHeader } from "./ReportPageHeader.js";
import { ReportRefreshButton } from "./ReportRefreshButton.js";
import { useOutgoingMessagesReport } from "./use-outgoing-messages-report.js";
import { useReportMessageDetail } from "./use-report-message-detail.js";
import { useReportTableLayout } from "./use-report-table-layout.js";
import { exportLocaleFromUi, useReportExport } from "./use-report-export.js";
import type { OutgoingReportFilterValues } from "./OutgoingReportFilters.js";

export function OutgoingMessagesReportPage({
  organizationId,
  role,
}: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const report = useOutgoingMessagesReport({ organizationId, role });
  const detail = useReportMessageDetail({
    organizationId,
    canAccess: report.canAccess,
  });
  const exportCtl = useReportExport();
  const layout = useReportTableLayout(OUTGOING_REPORT_COLUMNS);

  const onFiltersChange = (patch: Partial<OutgoingReportFilterValues>) => {
    if ("platform" in patch) report.setPlatform(patch.platform);
    if ("origin" in patch) report.setOrigin(patch.origin);
    if ("statusBucket" in patch) {
      report.setStatusBucket(patch.statusBucket ?? null);
    }
    if ("endpointId" in patch) report.setEndpointId(patch.endpointId);
    if ("campaignId" in patch) report.setCampaignId(patch.campaignId);
    if ("template" in patch) report.setTemplate(patch.template);
  };

  const onExport = (format: "csv" | "xlsx") => {
    const path = buildReportMessagesExportPath(organizationId, {
      direction: "OUTBOUND",
      fromDate: report.query.from,
      toDate: report.query.to,
      search: report.query.search || undefined,
      platform: report.query.platform,
      origin: report.query.origin,
      statusBucket: report.query.statusBucket,
      status: report.query.statusBucket ? undefined : report.query.status,
      endpointId: report.query.endpointId,
      campaignId: report.query.campaignId,
      template: report.query.template,
      sort: report.query.sort,
      format,
      locale: exportLocaleFromUi(i18n.language),
    });
    void exportCtl.runExport({
      pathWithQuery: path,
      fallbackFilename: `outgoing-messages.${format}`,
    });
  };

  const errorMessage =
    report.error == null
      ? null
      : isApiError(report.error) && report.error.status === 0
        ? t("reports.outgoing.backendUnavailable")
        : isApiError(report.error) && report.error.status === 403
          ? t("reports.outgoing.forbidden")
          : t("reports.outgoing.loadFailed");

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-hidden"
      data-testid="reports-outgoing-page"
    >
      <ReportPageChrome>
        <ReportPageHeader
          icon={ArrowUpRight}
          title={t("reports.outgoing.title")}
          description={t("reports.outgoing.helper")}
          actions={
            report.canAccess ? (
              <div className="flex items-center gap-2">
                <ReportRefreshButton
                  refreshing={Boolean(report.loading && report.list)}
                  disabled={report.loading}
                  onRefresh={() => {
                    report.refresh();
                    if (detail.messageId != null) detail.refresh();
                  }}
                  testId="reports-outgoing-refresh"
                />
                <div className="grid gap-1">
                  <ReportExportMenu
                    exporting={exportCtl.exporting}
                    onExport={onExport}
                    testId="reports-outgoing-export"
                  />
                  {exportCtl.exportError ? (
                    <p className="m-0 text-[0.75rem] text-danger" role="alert">
                      {exportCtl.exportError}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null
          }
        />

        <OutgoingReportToolbar
          query={report.query}
          dateFrom={report.dateFrom}
          dateTo={report.dateTo}
          options={report.options}
          filteredTotal={report.list?.totalCount ?? null}
          filtersActive={report.filtersActive}
          onSearchChange={report.setSearch}
          onDateRangeChange={report.setDateRange}
          onFiltersChange={onFiltersChange}
          onResetFilters={report.resetFilters}
          columnsSlot={
            <ReportColumnsPopover
              columnOrder={layout.columnOrder}
              hiddenColumns={layout.hiddenColumns}
              requiredColumns={OUTGOING_REPORT_COLUMNS.requiredColumns}
              labelFor={(id) => t(`reports.outgoing.columns.${id}`)}
              onToggle={layout.toggleColumn}
              onMove={layout.moveColumn}
              onReorder={layout.reorderColumn}
              onReset={layout.resetColumns}
              testIdPrefix="reports-outgoing"
            />
          }
        />
      </ReportPageChrome>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 py-3 md:overflow-hidden md:px-5">
        {errorMessage ? (
          <ErrorState
            layout="panel"
            message={errorMessage}
            onRetry={report.refresh}
            retryLabel={t("auth.retry")}
          />
        ) : null}

        {report.summary ? (
          <OutgoingReportSummary
            summary={report.summary}
            statusBucket={report.query.statusBucket}
            onSelectStatusBucket={report.setStatusBucket}
          />
        ) : report.loading ? (
          <div
            className="grid shrink-0 grid-cols-3 gap-1.5 sm:grid-cols-6"
            data-testid="reports-outgoing-summary-loading"
          >
            {Array.from({ length: 6 }, (_, index) => (
              <div
                key={index}
                className="h-12 animate-pulse rounded-md border border-line/70 bg-surface-muted motion-reduce:animate-none"
              />
            ))}
          </div>
        ) : null}

        <div className="flex min-h-[20rem] min-h-0 flex-1 flex-col md:min-h-0">
          <OutgoingReportTable
            list={report.list}
            loading={report.loading}
            page={report.query.page}
            pageSize={report.query.pageSize}
            emptyKind={outgoingReportEmptyKind(report.query)}
            visibleColumns={layout.visibleColumns}
            widths={layout.widths}
            onResizeColumn={layout.resizeColumn}
            onReorderColumn={layout.reorderColumn}
            onPageChange={report.setPage}
            onPageSizeChange={report.setPageSize}
            onResetFilters={report.resetFilters}
            onOpenMessage={detail.openMessage}
          />
        </div>
      </div>

      <MessageDetailDrawer
        open={detail.messageId != null}
        detail={detail.detail}
        loading={detail.loading}
        error={detail.error}
        onClose={detail.closeMessage}
        onRetry={detail.refresh}
      />
    </div>
  );
}
